import React, { useEffect, useState } from 'react';
import { streamsService, LearningStream } from '../services/streams';
import { logsService, DailyLog } from '../services/logs';
import { reflectionsService, WeeklyReflectionCreate } from '../services/reflections';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const WeeklyReview: React.FC = () => {
  const [streams, setStreams] = useState<LearningStream[]>([]);
  const [logs, setLogs] = useState<DailyLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviewNotes, setReviewNotes] = useState({
    hoursPerStream: {} as Record<string, number>,
    topicsCovered: '',
    completedTasks: '',
    gaps: '',
    adjustments: '',
  });

  useEffect(() => {
    loadWeeklyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadWeeklyData = async () => {
    try {
      const [streamsData, logsData] = await Promise.all([
        streamsService.getAll(),
        logsService.getAll(),
      ]);
      setStreams(streamsData);
      
      // Get logs from last 7 days
      const last7Days = logsData.filter(log => {
        const logDate = new Date(log.date);
        const daysAgo = (new Date().getTime() - logDate.getTime()) / (1000 * 60 * 60 * 24);
        return daysAgo <= 7;
      });
      setLogs(last7Days);
      
      // Calculate hours per stream
      const hoursPerStream: Record<string, number> = {};
      last7Days.forEach(log => {
        const streamName = streamsData.find(s => s.id === log.stream_id)?.name || 'Unknown';
        hoursPerStream[streamName] = (hoursPerStream[streamName] || 0) + log.hours_studied;
      });
      setReviewNotes({ ...reviewNotes, hoursPerStream });
    } catch (error) {
      console.error('Error loading weekly data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTimeAllocationData = () => {
    return Object.entries(reviewNotes.hoursPerStream).map(([name, value]) => ({
      name,
      value: Math.round(value),
    }));
  };

  const getWeeklyHoursData = () => {
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (6 - i));
      const dateStr = date.toISOString().split('T')[0];
      const dayLogs = logs.filter(log => log.date === dateStr);
      const totalHours = dayLogs.reduce((sum, log) => sum + log.hours_studied, 0);
      return {
        date: date.toLocaleDateString('en-US', { weekday: 'short' }),
        hours: totalHours,
      };
    });
    return last7Days;
  };

  const getAverageConfidence = (): number => {
    if (logs.length === 0) return 5;
    const totalConfidence = logs.reduce((sum, log) => sum + log.confidence_score, 0);
    return Math.round(totalConfidence / logs.length);
  };

  const getAverageMood = (): number => {
    if (logs.length === 0) return 5;
    const totalMood = logs.reduce((sum, log) => sum + log.mood_energy, 0);
    return Math.round(totalMood / logs.length);
  };

  const handleSubmitReflection = async () => {
    try {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6); // Saturday

      const reflectionData: WeeklyReflectionCreate = {
        week_start_date: startOfWeek.toISOString().split('T')[0],
        week_end_date: endOfWeek.toISOString().split('T')[0],
        topics_learned: reviewNotes.topicsCovered.split(',').filter(t => t.trim()).map(t => t.trim()),
        completed_tasks: reviewNotes.completedTasks.split(',').filter(t => t.trim()).map(t => t.trim()),
        knowledge_gaps: reviewNotes.gaps.split(',').filter(t => t.trim()).map(t => t.trim()),
        next_week_focus: reviewNotes.adjustments,
        confidence_level: getAverageConfidence(),
        energy_level: getAverageMood(),
        notes: `Weekly review for ${startOfWeek.toLocaleDateString()} - ${endOfWeek.toLocaleDateString()}`,
      };

      await reflectionsService.create(reflectionData);
      alert('Weekly reflection submitted successfully!');
      
      // Reset form
      setReviewNotes({
        hoursPerStream: {},
        topicsCovered: '',
        completedTasks: '',
        gaps: '',
        adjustments: '',
      });
    } catch (error) {
      console.error('Error submitting reflection:', error);
      alert('Failed to submit reflection. Please try again.');
    }
  };

  const COLORS = ['#3b82f6', '#22c55e', '#a855f7', '#f59e0b'];

  if (loading) {
    return <div className="p-6">Loading weekly review...</div>;
  }

  const totalHours = logs.reduce((sum, log) => sum + log.hours_studied, 0);
  const weeklyTarget = streams.reduce((sum, s) => sum + (s.target_hours / 52), 0); // Approximate weekly target

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Weekly Review
          </h1>
          <p className="text-gray-600">
            Week of {new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toLocaleDateString()} - {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-sm text-gray-600 mb-1">Total Hours</p>
            <p className="text-3xl font-bold">
              {totalHours.toFixed(1)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Target: {weeklyTarget.toFixed(1)}h
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-sm text-gray-600 mb-1">Average Confidence</p>
            <p className="text-3xl font-bold text-primary">
              {getAverageConfidence()}/10
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-sm text-gray-600 mb-1">Average Mood/Energy</p>
            <p className="text-3xl font-bold text-secondary">
              {getAverageMood()}/10
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-sm text-gray-600 mb-1">Days Logged</p>
            <p className="text-3xl font-bold text-accent">
              {new Set(logs.map(log => log.date)).size}/7
            </p>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Time Allocation */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4">Time Allocation by Stream</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={getTimeAllocationData()}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry: any) => `${entry.name} ${(entry.percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {getTimeAllocationData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Daily Hours */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4">Daily Study Hours</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={getWeeklyHoursData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="hours" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Stream-by-Stream Summary */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">Stream Summary</h3>
          <div className="space-y-4">
            {streams.map((stream) => {
              const streamLogs = logs.filter(log => log.stream_id === stream.id);
              const streamHours = streamLogs.reduce((sum, log) => sum + log.hours_studied, 0);
              const streamTarget = stream.target_hours / 52; // Weekly target
              const progress = streamTarget > 0 ? (streamHours / streamTarget) * 100 : 0;
              
              return (
                <div key={stream.id} className="border-b pb-4 last:border-0">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold">{stream.name}</h4>
                    <span className="text-sm text-gray-600">
                      {streamHours.toFixed(1)}h / {streamTarget.toFixed(1)}h
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        progress >= 100 ? 'bg-green-500' : progress >= 70 ? 'bg-primary' : 'bg-yellow-500'
                      }`}
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                  </div>
                  <div className="mt-2 text-sm text-gray-600">
                    Status: <span className="font-medium">{stream.status}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Review Form */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">Weekly Reflection</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Topics Covered This Week</label>
              <textarea
                value={reviewNotes.topicsCovered}
                onChange={(e) => setReviewNotes({ ...reviewNotes, topicsCovered: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                rows={3}
                placeholder="What topics did you cover this week?"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Completed Tasks</label>
              <textarea
                value={reviewNotes.completedTasks}
                onChange={(e) => setReviewNotes({ ...reviewNotes, completedTasks: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                rows={2}
                placeholder="What tasks did you complete?"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Gaps / Areas Behind</label>
              <textarea
                value={reviewNotes.gaps}
                onChange={(e) => setReviewNotes({ ...reviewNotes, gaps: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                rows={2}
                placeholder="What areas are you behind on?"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Adjustments for Next Week</label>
              <textarea
                value={reviewNotes.adjustments}
                onChange={(e) => setReviewNotes({ ...reviewNotes, adjustments: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                rows={3}
                placeholder="What adjustments will you make next week?"
              />
            </div>
            <button
              onClick={handleSubmitReflection}
              className="w-full px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-semibold"
            >
              Submit Weekly Reflection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklyReview;

